import React, { useState } from 'react';
import { colors } from '../../styles/tokens';
import type { NegadoProductoRow } from '../../types/indicadores.types';
import { fmtInt } from '../../utils/format';
import { useEsMovil } from '../../hooks/useEsMovil';
import { estatusBadgeColors } from '../../utils/negados';
import { useTableSort, type SortDir } from '../../utils/sort';
import { buildNegadosTiendaSheet, exportXlsx } from '../../utils/exportXlsx';
import { ProductPhoto } from '../global/NegadosCards';
import ExportButton from '../ExportButton';
import ModalEnfoque from '../ModalEnfoque';

export interface NegadosTiendaTableProps {
  /**
   * `GET /dashboard/negados-productos` SCOPEADO a la tienda seleccionada de
   * Mi tienda (el server fuerza `chain`/`branchOffice` de la sesión para
   * `branch_office`; `IndicadoresScreen` los manda igual que `getTienda`).
   */
  productos: NegadoProductoRow[];
  /**
   * Clic en una fila: dispara con el `productId` de esa fila (a diferencia
   * de `NegadosCards`, que dispara con la fila completa) — preparado para el
   * drill por talla/día de la tarea siguiente (AD-3). Opcional para no
   * romper a consumidores/pruebas que no les importa el drill.
   */
  onSelectProducto?: (productId: string) => void;
  /**
   * Fase D (plan `2026-07-22-detalle-por-modelo-y-export-xlsx-plan`, espejo
   * del app): rango YA activo en Mi tienda — solo para nombrar el archivo
   * del export a Excel (`negados-tienda_<desde>_<hasta>.xlsx`); sin rango
   * cae a `negados-tienda.xlsx`.
   */
  rango?: { desde: string; hasta: string } | null;
  /**
   * `true` cuando la vista está filtrada por colaborador: esta tabla es de toda
   * la tienda (el endpoint no filtra por vendedor) y tiene que decirlo, porque
   * la ayuda del filtro dice que acota toda la vista.
   */
  conColaborador?: boolean;
}

const thBase: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  color: colors.texto.quaternary,
  fontWeight: 700,
};

const theadRowStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: '#fff',
  boxShadow: 'inset 0 -1px 0 #e7e9ef',
};

const cardStyle: React.CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.border}`,
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(20,28,54,.05)',
};

/**
 * Barra de título de la tarjeta. En teléfono el relleno lateral baja a 14 px
 * (el margen que usa toda la vista en móvil); en escritorio queda igual.
 */
function estiloTituloTarjeta(esMovil: boolean): React.CSSProperties {
  return {
    background: colors.brand,
    color: '#fff',
    padding: esMovil ? '12px 14px' : '12px 20px',
    fontWeight: 800,
    fontSize: 14,
    letterSpacing: '.02em',
    // Los controles viven DENTRO de la barra, igual que en `NegadosCards` de
    // la vista Global: ver el porqué en el comentario de `contenido`.
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    ...(esMovil ? { flexWrap: 'wrap' as const } : null),
  };
}

interface ColumnaOrdenable {
  key: keyof NegadoProductoRow;
  label: string;
  style: React.CSSProperties;
  testId: string;
}

const COLUMNAS: ColumnaOrdenable[] = [
  { key: 'descripcion', label: 'Descripción', style: { ...thBase, textAlign: 'left', padding: '10px 16px' }, testId: 'th-tienda-negados-descripcion' },
  { key: 'status', label: 'Estatus', style: { ...thBase, textAlign: 'left', padding: '10px 10px' }, testId: 'th-tienda-negados-status' },
  { key: 'negados', label: 'Negados', style: { ...thBase, textAlign: 'right', padding: '10px 12px' }, testId: 'th-tienda-negados-negados' },
];

function renderSortableTh(
  col: ColumnaOrdenable,
  sort: { key: keyof NegadoProductoRow; dir: SortDir },
  toggle: (key: keyof NegadoProductoRow) => void,
): React.ReactElement {
  return (
    <th
      key={String(col.key)}
      data-testid={col.testId}
      role="button"
      tabIndex={0}
      onClick={() => toggle(col.key)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle(col.key);
        }
      }}
      style={{ ...col.style, cursor: 'pointer', userSelect: 'none' }}
    >
      {col.label}
      {sort.key === col.key && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
    </th>
  );
}

/**
 * El equivalente MÓVIL del encabezado ordenable: un chip tocable de 44 px.
 *
 * En teléfono la tabla desaparece (una tarjeta por fila, ver abajo) y con
 * ella se irían los `th` que ordenan. Estos chips llevan **el mismo
 * `data-testid` y llaman al mismo `toggle`** que su `th` de escritorio, así
 * que el control de orden no se pierde y nada que dependa de esos
 * identificadores se rompe. Nunca coexisten: o se pinta la tabla, o la lista.
 */
function renderSortChipMovil(
  col: ColumnaOrdenable,
  sort: { key: keyof NegadoProductoRow; dir: SortDir },
  toggle: (key: keyof NegadoProductoRow) => void,
): React.ReactElement {
  const activo = sort.key === col.key;
  return (
    <button
      key={String(col.key)}
      type="button"
      data-testid={col.testId}
      onClick={() => toggle(col.key)}
      style={{
        minHeight: 44,
        padding: '10px 13px',
        borderRadius: 999,
        border: `1px solid ${activo ? colors.brandSoftBorder : colors.border}`,
        background: activo ? colors.brandSoftBg : colors.card,
        color: activo ? colors.brand : colors.texto.secondary,
        fontFamily: 'inherit',
        fontSize: 12.5,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {col.label}
      {activo && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
    </button>
  );
}

/** Par etiqueta/valor de las tarjetas móviles (11 px la etiqueta, 14 px la cifra: los mínimos legibles). */
const ParMovil: React.FC<{ etiqueta: string; valor: React.ReactNode; color?: string }> = ({
  etiqueta,
  valor,
  color,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        color: colors.texto.quaternary,
      }}
    >
      {etiqueta}
    </span>
    <span
      style={{
        fontSize: 14,
        fontWeight: 800,
        color: color ?? colors.texto.strong,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {valor}
    </span>
  </div>
);

/** Contenedor de los chips de orden en móvil (una sola fila envolvente, bajo el título). */
const barraOrdenMovil: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  background: colors.footerBg,
  borderBottom: `1px solid ${colors.rowBorder}`,
};

const etiquetaOrdenMovil: React.CSSProperties = {
  width: '100%',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  color: colors.texto.quaternary,
};

/**
 * Sección "Productos más negados" de la vista **Mi tienda** (Ajuste B #4,
 * `2026-07-22-tablero-ajustes-vendedor-negados-tienda-design.md`): mismo
 * patrón visual/columnas (descripción+ICOD, estatus con color, negados,
 * foto) que `NegadosCards` de la vista Global, pero acotado a UNA sola tabla
 * — la vista Mi tienda NO necesita el "Detalle de negados por talla" (ese
 * desglose por talla/día vive en el drill modal de AD-3, no como tabla
 * aparte). Por eso NO se reusa `NegadosCards` tal cual (siempre renderiza
 * las 2 tarjetas juntas): este wrapper delgado SÍ reusa sus piezas
 * exportadas (`ProductPhoto`) y los mismos utils (`estatusBadgeColors`,
 * `useTableSort`, `fmtInt`) en vez de duplicar esa lógica.
 *
 * Ordenada desc por `negados` al montar (mismo default que `NegadosCards`);
 * clic en un encabezado reordena solo estas filas ya cargadas (top N que ya
 * trae el backend). Clic en una fila dispara `onSelectProducto(productId)` —
 * el drill de AD-3 lo consumirá para abrir el modal por talla/día.
 *
 * EN TELÉFONO deja de ser una tabla: sus 4 columnas (descripción, estatus,
 * negados y foto) no caben en 390 px y arrastrar de lado para leer una cifra
 * es la salida perezosa. Cada fila se pinta como una tarjeta —foto y nombre
 * primero, luego ICOD, estatus y negados— y el orden se controla con los
 * chips de arriba, que llaman al mismo `toggle` que los encabezados. Los
 * datos, el orden, el drill y el export son los mismos: sólo cambia la
 * disposición.
 */
const NegadosTiendaTable: React.FC<NegadosTiendaTableProps> = ({
  productos,
  onSelectProducto,
  rango,
  conColaborador = false,
}) => {
  const esMovil = useEsMovil();
  const { sorted, sort, toggle } = useTableSort(productos, 'negados', 'desc');
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  // Modo enfoque (el "Expandir" de Power BI). Ver `ModalEnfoque`.
  const [enfocado, setEnfocado] = useState(false);
  const hasProductos = productos.length > 0;

  /** Fase D: export a Excel de la hoja "Negados" (mismo `productos` ya cargado, sin re-fetch). */
  function handleExport() {
    const filename = rango ? `negados-tienda_${rango.desde}_${rango.hasta}.xlsx` : 'negados-tienda.xlsx';
    return exportXlsx([buildNegadosTiendaSheet(productos)], filename);
  }

  /*
   * El botón de exportar va envuelto en un contenedor de 44 px de alto en
   * teléfono (`alignItems:'stretch'` estira al botón), porque `ExportButton`
   * lo comparten otras cuatro vistas y subirle la altura a todas no es este
   * cambio. En escritorio el envoltorio usa `display:'contents'`: NO genera
   * caja, así que el botón sigue siendo hijo directo de su contenedor y no se
   * mueve un píxel.
   */
  const botonExport = (
    <span
      style={esMovil ? { display: 'inline-flex', alignItems: 'stretch', minHeight: 44 } : { display: 'contents' }}
    >
      {/* `dark` porque el botón vive dentro de la barra azul: con la variante
          clara el icono se pintaba del gris del texto y desaparecía. */}
      <ExportButton
        testid="negados-tienda"
        onExport={handleExport}
        disabled={!hasProductos}
        variant="dark"
      />
    </span>
  );

  const contenido = (
    <div
      data-testid="negados-tienda-section"
      style={{
        marginTop: enfocado ? 0 : 18,
        ...(enfocado ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '0 0 0 0' } : {}),
      }}
    >
      {/* ⚠️ AQUÍ HABÍA UN SEGUNDO TÍTULO. Esta sección pintaba «Productos más
          negados» dos veces: un <h3> sobre el fondo gris y otra vez dentro de
          la barra azul de la tarjeta, a 30 px de distancia. La vista Global
          nunca lo hizo así — `NegadosCards` lleva el título y sus controles
          dentro de la barra, y nada encima. Se unifica con esa. */}
      {!hasProductos && (
        <div
          data-testid="negados-tienda-empty"
          style={{
            marginTop: 11,
            background: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: esMovil ? '24px 14px' : '28px 20px',
            textAlign: 'center',
            color: colors.texto.tertiary,
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          Sin negados en el periodo para esta tienda.
        </div>
      )}

      {hasProductos && (
        <div data-testid="negados-tienda-productos" style={{ ...cardStyle, marginTop: enfocado ? 0 : 11, ...(enfocado ? { border: 'none', borderRadius: 0, boxShadow: 'none' } : {}), ...(enfocado ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}) }}>
          {!enfocado && (
            <div style={estiloTituloTarjeta(esMovil)}>
              <span>
                Top 40 · Productos más negados
                <span
                  data-testid="negados-tienda-alcance"
                  style={{ display: 'block', marginTop: 2, fontSize: 11.5, fontWeight: 500, letterSpacing: 0, opacity: 0.85 }}
                >
                  {conColaborador
                    ? 'De toda la tienda · no se acota por colaborador'
                    : 'De toda la tienda'}
                </span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  data-testid="negados-tienda-enfocar"
                  onClick={() => setEnfocado(true)}
                  aria-label="Expandir a pantalla completa"
                  title="Expandir"
                  style={{
                    background: 'rgba(255,255,255,.14)',
                    border: '1px solid rgba(255,255,255,.3)',
                    borderRadius: 8,
                    color: '#fff',
                    fontSize: esMovil ? 15 : 13,
                    lineHeight: 1,
                    fontWeight: 700,
                    // 28 px de alto es imposible de acertar con el dedo: en
                    // móvil sube a 44, el mínimo recomendado.
                    padding: esMovil ? '10px 16px' : '7px 10px',
                    cursor: 'pointer',
                    ...(esMovil ? { minHeight: 44 } : null),
                  }}
                >
                  ⤢
                </button>
                {botonExport}
              </span>
            </div>
          )}
          {esMovil && (
            <div style={barraOrdenMovil} role="group" aria-label="Ordenar productos más negados">
              <span style={etiquetaOrdenMovil}>Ordenar por</span>
              {COLUMNAS.map((col) => renderSortChipMovil(col, sort, toggle))}
            </div>
          )}
          <div
            style={{
              // En móvil cada fila ocupa dos o tres líneas, así que el área
              // desplazable sube de 260 a 340 px; `overflowX: hidden`
              // garantiza que una descripción larga no arrastre la tarjeta de
              // lado.
              maxHeight: enfocado ? undefined : esMovil ? 340 : 260,
              flex: enfocado ? 1 : undefined,
              minHeight: 0,
              overflow: 'auto',
              ...(esMovil ? { overflowX: 'hidden' as const } : null),
            }}
          >
            {esMovil ? (
              sorted.map((p, i) => {
                const { bg, text } = estatusBadgeColors(p.status);
                const rowKey = `${p.productId}|${p.status}|${i}`;
                return (
                  <div
                    key={rowKey}
                    data-testid={`fila-negado-tienda-${rowKey}`}
                    onClick={() => onSelectProducto?.(p.productId)}
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                      padding: '12px 14px',
                      borderBottom: `1px solid ${colors.rowBorder}`,
                      cursor: 'pointer',
                    }}
                  >
                    <ProductPhoto imageUrl={p.imageUrl} alt={p.descripcion} />
                    {/* `minWidth: 0` o el flex se niega a encoger este bloque y
                        la descripción larga empuja la tarjeta fuera de la
                        pantalla. */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13.5,
                          color: colors.texto.strong,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {p.descripcion}
                      </div>
                      <div style={{ fontSize: 11, color: colors.texto.quaternary, fontWeight: 600, marginTop: 2 }}>
                        ICOD {p.icod}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'space-between',
                          gap: 10,
                          marginTop: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            fontSize: 11,
                            fontWeight: 700,
                            padding: '4px 9px',
                            borderRadius: 6,
                            background: bg,
                            color: text,
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {p.status}
                        </span>
                        <ParMovil etiqueta="Negados" valor={fmtInt(p.negados)} color={colors.rojo} />
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={theadRowStyle}>
                    {COLUMNAS.map((col) => renderSortableTh(col, sort, toggle))}
                    <th style={{ padding: '10px 16px' }} />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((p, i) => {
                    const { bg, text } = estatusBadgeColors(p.status);
                    const rowKey = `${p.productId}|${p.status}|${i}`;
                    const hovered = hoveredKey === rowKey;
                    return (
                      <tr
                        key={rowKey}
                        data-testid={`fila-negado-tienda-${rowKey}`}
                        onClick={() => onSelectProducto?.(p.productId)}
                        onMouseEnter={() => setHoveredKey(rowKey)}
                        onMouseLeave={() => setHoveredKey(null)}
                        style={{
                          cursor: 'pointer',
                          borderBottom: `1px solid ${colors.rowBorder}`,
                          background: hovered ? colors.rowHover : 'transparent',
                        }}
                      >
                        <td style={{ padding: '11px 16px' }}>
                          <div style={{ fontWeight: 700, color: colors.texto.strong }}>{p.descripcion}</div>
                          <div style={{ fontSize: 11, color: colors.texto.quaternary, fontWeight: 600, marginTop: 1 }}>
                            ICOD {p.icod}
                          </div>
                        </td>
                        <td style={{ padding: '11px 10px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              fontSize: 10.5,
                              fontWeight: 700,
                              padding: '3px 8px',
                              borderRadius: 6,
                              background: bg,
                              color: text,
                            }}
                          >
                            {p.status}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '11px 12px',
                            textAlign: 'right',
                            fontWeight: 800,
                            color: colors.rojo,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {fmtInt(p.negados)}
                        </td>
                        <td style={{ padding: '8px 16px' }}>
                          <ProductPhoto imageUrl={p.imageUrl} alt={p.descripcion} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (!enfocado) return contenido;

  return (
    <ModalEnfoque
      testid="negados-tienda"
      titulo="Top 40 · Productos más negados"
      subtitulo={`${productos.length} producto${productos.length === 1 ? '' : 's'} en el periodo`}
      acciones={botonExport}
      onClose={() => setEnfocado(false)}
    >
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {contenido}
      </div>
    </ModalEnfoque>
  );
};

export default NegadosTiendaTable;
